<template>
  <view class="v-form-container">
    <view v-if="renderFields && renderFields.length > 0">
      <view v-for="(item, index) in renderFields" :key="index" class="form-item">
        <!-- 标签显示 -->
        <view class="label" v-if="item.__config__ && item.__config__.showLabel !== false">
          <text v-if="item.__config__.required" class="required">*</text>
          {{ item.__config__.label }}
        </view>
        
        <view class="input-container">
          <!-- 文本输入 / 密码输入 -->
          <input 
            v-if="item.__config__.tag === 'el-input' && item.type !== 'textarea'"
            :type="item.type === 'password' ? 'password' : 'text'"
            v-model="formData[item.__vModel__]"
            :placeholder="item.placeholder"
            :disabled="disabled || item.disabled"
            class="uni-input"
          />
          
          <!-- 多行文本 -->
          <textarea 
            v-else-if="item.__config__.tag === 'el-input' && item.type === 'textarea'"
            v-model="formData[item.__vModel__]"
            :placeholder="item.placeholder"
            :disabled="disabled || item.disabled"
            class="uni-textarea"
            auto-height
          />
          
          <!-- 选择器 (模拟 el-select) -->
          <picker 
            v-else-if="item.__config__.tag === 'el-select' || item.__config__.tag === 'el-radio-group'"
            @change="(e) => onPickerChange(e, item)"
            :value="getPickerIndex(item)"
            :range="getOptions(item)"
            range-key="label"
            :disabled="disabled || item.disabled"
          >
            <view class="picker-value">
              {{ getPickerLabel(item) || item.placeholder || '请选择' }}
            </view>
          </picker>
          
          <!-- 开关 (模拟 el-switch) -->
          <switch 
            v-else-if="item.__config__.tag === 'el-switch'"
            :checked="formData[item.__vModel__]"
            @change="(e) => formData[item.__vModel__] = e.detail.value"
            :disabled="disabled || item.disabled"
            color="#26A69A"
          />

          <!-- 日期选择 -->
          <picker 
            v-else-if="item.__config__.tag === 'el-date-picker'"
            mode="date"
            :value="formData[item.__vModel__]"
            @change="(e) => formData[item.__vModel__] = e.detail.value"
            :disabled="disabled || item.disabled"
          >
            <view class="picker-value">
              {{ formData[item.__vModel__] || item.placeholder || '请选择日期' }}
            </view>
          </picker>
          
          <!-- 默认展示值 (只读模式或不支持的组件) -->
          <view v-else class="readonly-value">
            {{ formData[item.__vModel__] || '暂无数据' }}
          </view>
        </view>
      </view>
    </view>
    <view v-else class="empty-tip">
      <text v-if="loading">解析中...</text>
      <text v-else>表单配置为空或解析失败</text>
    </view>
  </view>
</template>

<script>
export default {
  name: 'VForm',
  props: {
    // 接受对象或JSON字符串
    formConf: {
      type: [Object, String],
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {},
      renderFields: [],
      loading: false
    }
  },
  watch: {
    formConf: {
      handler(val) {
        this.parseFormConf(val);
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    /**
     * 解析表单配置
     */
    parseFormConf(val) {
      if (!val) {
        this.renderFields = [];
        return;
      }

      let conf = val;
      if (typeof val === 'string') {
        try {
          conf = JSON.parse(val);
        } catch (e) {
          console.error('VForm: JSON解析失败', e);
          this.renderFields = [];
          return;
        }
      }

      if (conf && conf.fields) {
        this.renderFields = conf.fields;
        this.initFormData();
      } else {
        this.renderFields = [];
      }
    },

    /**
     * 初始化表单数据默认值
     */
    initFormData() {
      const data = {};
      this.renderFields.forEach(field => {
        if (field.__vModel__) {
          // 只有当当前没有值时才使用默认值，避免覆盖回显数据
          if (this.formData[field.__vModel__] === undefined) {
            data[field.__vModel__] = field.__config__.defaultValue;
          }
        }
      });
      this.formData = Object.assign({}, data, this.formData);
    },
    
    /**
     * API: 设置表单数据（回显用）
     */
    setFormData(data) {
      if (!data) return;
      this.formData = Object.assign({}, this.formData, data);
    },
    
    /**
     * API: 获取表单数据
     */
    getFormData() {
      return JSON.parse(JSON.stringify(this.formData));
    },
    
    /**
     * API: 校验表单
     */
    validateForm() {
      let isValid = true;
      for (const field of this.renderFields) {
        if (field.__config__ && field.__config__.required) {
          const value = this.formData[field.__vModel__];
          if (value === undefined || value === null || value === '') {
            isValid = false;
            break;
          }
        }
      }
      return isValid;
    },

    /**
     * 获取选项列表
     */
    getOptions(item) {
      if (item.__slot__ && item.__slot__.options) {
        return item.__slot__.options;
      }
      return [];
    },
    
    onPickerChange(e, item) {
      const index = e.detail.value;
      const options = this.getOptions(item);
      const option = options[index];
      if (option) {
        this.formData[item.__vModel__] = option.value;
      }
    },
    
    getPickerIndex(item) {
      const value = this.formData[item.__vModel__];
      const options = this.getOptions(item);
      if (!options.length) return -1;
      return options.findIndex(opt => opt.value === value);
    },
    
    getPickerLabel(item) {
      const value = this.formData[item.__vModel__];
      const options = this.getOptions(item);
      if (!options.length) return '';
      const option = options.find(opt => opt.value === value);
      return option ? option.label : '';
    }
  }
}
</script>

<style scoped>
.v-form-container {
  padding: 10rpx 0;
}

.form-item {
  margin-bottom: 30rpx;
}

.label {
  font-size: 28rpx;
  color: #333;
  margin-bottom: 12rpx;
  font-weight: 500;
  display: block;
}

.required {
  color: #ff4d4f;
  margin-right: 4rpx;
}

.input-container {
  background-color: #f9f9f9;
  border-radius: 8rpx;
  padding: 20rpx;
  min-height: 40rpx;
}

.uni-input {
  height: 40rpx;
  font-size: 28rpx;
  width: 100%;
}

.uni-textarea {
  width: 100%;
  font-size: 28rpx;
  min-height: 100rpx;
  line-height: 1.5;
}

.picker-value {
  font-size: 28rpx;
  color: #333;
  min-height: 40rpx;
}

.readonly-value {
  font-size: 28rpx;
  color: #666;
}

.empty-tip {
  padding: 40rpx;
  text-align: center;
  color: #999;
  font-size: 24rpx;
}
</style>
